import classNames from 'classnames/bind';
import ReactBaseSelect, { StylesConfig } from 'react-select';
import { ShadowStyle } from 'consts/shadow';
import { getInputBorderStyle } from 'lib/BaseInput';
import { CodeNameModel } from 'components/common/types';
import { InputShape, InputSize, InputTheme } from 'components/common/types/Input.types';
import styles from 'lib/AsyncSelect/AsyncSelect.pcss';
import { asyncSelectThemeStyles } from 'lib/AsyncSelect/AsyncSelect.styles';
import { BaseSelectProps, OptionObject, Padding } from '../BaseSelect.types';

const cx = classNames.bind(styles);

const BaseSelectWrapper = ({
  defaultOptions,
  options,
  name,
  placeholder,
  selectedValue,
  onSelect,
  dataTestId,
  isMultiple,
  maxMenuHeight,
  customComponents = {},
  closeMenuOnSelect = true,
  isDisabled = false,
  isClearable = false,
  isSelectionVisible = true,
  isPlaceholderVisible = true,
  menuPosition = 'absolute',
  menuPlacement = 'bottom',
  shadow = ShadowStyle.SMALL,
  shape = InputShape.ROUNDED_MEDIUM,
  padding = Padding.DEFAULT,
  withPortal = true,
  isSearchable = false,
  theme = InputTheme.LIGHT,
  classNameWrapper,
  size = InputSize.MEDIUM,
  dataPendoId,
  menuIsOpen,
  onMenuOpen,
  onMenuClose,
  customPortalTarget,
}: BaseSelectProps): React.ReactElement => {
  const handleChange = (newValue: CodeNameModel[] | CodeNameModel): void => {
    onSelect(name, newValue);
  };

  const getOptionLabel = (option: OptionObject): string => {
    return option.name;
  };

  const getOptionValue = (option: OptionObject): string => {
    return option.code;
  };

  const getStyles = (): StylesConfig => {
    const isAutoComplete = (defaultOptions && defaultOptions?.length) || options.length;

    return {
      control: (base) => ({
        ...base,
        ...asyncSelectThemeStyles.control[theme],
        ...asyncSelectThemeStyles.size.control[size],
      }),
      input: (base) => ({
        ...base,
        ...asyncSelectThemeStyles.input[theme],
        ...asyncSelectThemeStyles.size.input[size],
      }),
      dropdownIndicator: (base) => ({
        ...base,
        display: isAutoComplete ? base.display : 'none',
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 1000,
        marginTop: '0.25rem',
        fontSize: 'small',
        ...(customPortalTarget && { position: 'absolute', top: '100%', left: 0, right: 'auto', width: '100%' }),
      }),
      placeholder: (base) => ({
        ...base,
        opacity: isPlaceholderVisible ? 1 : 0,
      }),
    };
  };

  return (
    <div
      className={cx(
        { 'shadow-inner-sm': shadow !== ShadowStyle.NONE },
        { 'shadow-none': shadow === ShadowStyle.NONE },
        getInputBorderStyle({
          isDisabled,
          shape,
          theme,
        }),
        classNameWrapper,
      )}
      data-test-id={dataTestId}
      data-pendo-id={dataPendoId}
    >
      <ReactBaseSelect
        id={`react-select-${name}`}
        aria-label={name}
        className={cx(
          'w-full',
          padding,
          {
            'async-select-shadow': shadow === ShadowStyle.SMALL,
          },
          { 'async-select': theme === InputTheme.LIGHT },
          { 'async-select-flat-gray': theme === InputTheme.FLAT_GRAY },
          { 'async-select-white-with-border': theme === InputTheme.WHITE_WITH_BORDER },
        )}
        classNamePrefix="react-select"
        components={customComponents}
        controlShouldRenderValue={isSelectionVisible}
        options={options}
        value={selectedValue}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        hideSelectedOptions={false}
        isMulti={isMultiple}
        isDisabled={isDisabled}
        isClearable={isClearable}
        menuPosition={menuPosition}
        menuPlacement={menuPlacement}
        maxMenuHeight={maxMenuHeight}
        onChange={handleChange}
        placeholder={placeholder}
        styles={getStyles()}
        menuPortalTarget={withPortal ? (customPortalTarget ?? document.body) : undefined}
        closeMenuOnSelect={closeMenuOnSelect}
        isSearchable={isSearchable}
        menuIsOpen={menuIsOpen}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
      />
    </div>
  );
};

export default BaseSelectWrapper;
